import { FitnessPlan, TrainingProgramBackup } from '@cf-shared/data-model/fitness-plan';
import { repairProgramFormatAtLoad } from './program-format-repair';

/**
 * Bring the stored as-built copy of the program (`generatedPlanBackup`) into today's
 * format, using the same repairs `userDb.loadPlanAsync` applies to the live program, so the
 * rebuild warning's comparison does not read an old account as customised (df-dadb3d927571).
 *
 * "The same repairs" is now literal: both paths call `repairProgramFormatAtLoad`, which is the
 * one definition of the sequence. They used to be two hand-kept copies of it (df-0c02f36b5c60).
 *
 * The copy is repaired against ITS OWN days, never by copying what the live repairs did, so
 * a sub-slot a person really removed still reads as an edit. Why, and why it reports
 * nothing: `design-docs/exercise-replacement.md`, "Restoring the built program".
 *
 * Returns whether the copy changed, so the caller knows to save.
 */
export function normalizeProgramBackupAtLoad(backup: TrainingProgramBackup | undefined): boolean {
  // a missing or damaged copy has nothing to repair; the comparison already treats a
  // missing one as "nothing known"
  if (!backup || !Array.isArray(backup.workouts) || !backup.subSlots || typeof backup.subSlots !== 'object') {
    return false;
  }

  // the copy is only ever taken from a built program, so it is passed as one: that keeps
  // gcOrphanSubslots's #4587 guard, which refuses to empty a program whose days reference
  // nothing at all
  const asBuiltProgram = { ready: true, workouts: backup.workouts, subSlots: backup.subSlots } as FitnessPlan;
  const result = repairProgramFormatAtLoad(asBuiltProgram);

  // the repair may replace the day list outright (dropping days that are not objects), and
  // that new array lives on the stand-in above, so it is carried back onto the copy itself.
  // The sub-slot dictionary is only ever mutated in place, so it needs no such copy-back.
  backup.workouts = asBuiltProgram.workouts;

  // what it stripped out is deliberately dropped here. The copy is not the program the user
  // trains from, and the same damage on the live program is already reported by the live path
  return result.changed;
}
